/**
 * Programmatic Cap (trycap.dev) captcha hook.
 *
 * Cap exposes two integration modes; we use the "programmatic" one
 * (https://trycap.dev/guide/programmatic.html) — there is no visible
 * widget, no checkbox, no user interaction. We instantiate `new Cap(...)`,
 * call `solve()` to run the proof-of-work in a hidden web worker, and
 * receive a one-shot token suitable for `/siteverify` on our backend.
 *
 * UX strategy:
 *   - As soon as captcha is known to be enabled, kick off a solve in
 *     the background. By the time the user finishes typing email +
 *     password, the token is usually already minted.
 *   - `getToken()` is what onSubmit awaits. If a solve is already in
 *     flight, it awaits that promise instead of starting a new one;
 *     if a previous solve already produced a token, returns it.
 *   - On any submit failure we call `reset()` to discard the spent
 *     token (Cap tokens are single-use after `/siteverify` redeems
 *     them) and pre-solve a fresh one for the retry.
 *
 * The `@cap.js/widget` package is loaded via dynamic `import(...)` so
 * the PoW worker bundle (~150KB minified) doesn't ship to users when
 * captcha is disabled.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicCaptchaConfigDTO } from '@bruno/shared';
import type { Cap as CapClass } from '@cap.js/widget';

export type CaptchaStatus = 'disabled' | 'idle' | 'solving' | 'ready' | 'error';

export interface CaptchaState {
  status: CaptchaStatus;
  token: string | null;
  /** 0..100 — populated by Cap's `progress` event. */
  progress: number;
  error?: string;
}

const INITIAL: CaptchaState = { status: 'idle', token: null, progress: 0 };

export function useCapCaptcha(cfg: PublicCaptchaConfigDTO | undefined | null) {
  const [state, setState] = useState<CaptchaState>(INITIAL);
  const capRef = useRef<CapClass | null>(null);
  const solvingRef = useRef<Promise<string | null> | null>(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Recreate the Cap instance only when the endpoint changes (admin
  // changed Instance URL or Site Key while the page was open).
  const endpoint = cfg?.enabled ? `${cfg.instanceUrl}/${cfg.siteKey}/` : null;
  useEffect(() => {
    capRef.current = null;
    solvingRef.current = null;
    if (mountedRef.current) setState(cfg?.enabled ? INITIAL : { ...INITIAL, status: 'disabled' });
  }, [endpoint, cfg?.enabled]);

  const solve = useCallback(async (): Promise<string | null> => {
    if (!cfg?.enabled || !endpoint) {
      if (mountedRef.current) setState({ ...INITIAL, status: 'disabled' });
      return null;
    }

    // De-duplicate concurrent solve requests.
    if (solvingRef.current) return solvingRef.current;

    const promise = (async () => {
      try {
        const mod = await import('@cap.js/widget');
        const Cap = (mod as { Cap?: typeof CapClass; default?: typeof CapClass }).Cap
          ?? (mod as { default?: typeof CapClass }).default;
        if (!Cap) throw new Error('failed to load Cap widget module');

        let cap = capRef.current;
        if (!cap) {
          cap = new Cap({ apiEndpoint: endpoint });
          capRef.current = cap;
          cap.addEventListener('progress', (e) => {
            if (!mountedRef.current) return;
            const pct = Math.max(0, Math.min(100, Number(e.detail.progress) || 0));
            setState((s) => (s.status === 'solving' ? { ...s, progress: pct } : s));
          });
          cap.addEventListener('error', (e) => {
            if (!mountedRef.current) return;
            setState({ status: 'error', token: null, progress: 0, error: e.detail.message });
          });
        }

        if (mountedRef.current) setState({ status: 'solving', token: null, progress: 0 });
        const result = await cap.solve();
        if (!result?.success || !result.token) throw new Error('cap solve returned no token');
        if (mountedRef.current) {
          setState({ status: 'ready', token: result.token, progress: 100 });
        }
        return result.token;
      } catch (err) {
        if (mountedRef.current) {
          setState({
            status: 'error',
            token: null,
            progress: 0,
            error: err instanceof Error ? err.message : String(err),
          });
        }
        return null;
      } finally {
        solvingRef.current = null;
      }
    })();
    solvingRef.current = promise;
    return promise;
  }, [cfg?.enabled, endpoint]);

  const reset = useCallback(() => {
    try {
      capRef.current?.reset();
    } catch {
      // The widget may throw if it was never solved; safe to ignore.
    }
    solvingRef.current = null;
    if (mountedRef.current) setState(cfg?.enabled ? INITIAL : { ...INITIAL, status: 'disabled' });
    // Pre-solve a fresh token in the background so the retry doesn't
    // have to wait through proof-of-work after a wrong-password 401.
    if (cfg?.enabled) void solve();
  }, [cfg?.enabled, solve]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!cfg?.enabled) return null;
    if (state.token) return state.token;
    if (solvingRef.current) return solvingRef.current;
    return solve();
  }, [cfg?.enabled, solve, state.token]);

  // Pre-solve as soon as we know captcha is enabled / endpoint changes.
  useEffect(() => {
    if (cfg?.enabled && endpoint) void solve();
  }, [cfg?.enabled, endpoint, solve]);

  return { state, getToken, reset };
}
